import { supabase } from './supabase';
import GTMService from './gtm';

// Type definitions for referral system
export interface ReferralCode {
  id: string;
  user_id: string;
  referral_code: string;
  is_active: boolean | null;
  uses_count: number | null;
  max_uses: number | null;
  expires_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ReferralReward {
  id: string;
  referrer_id: string;
  referred_user_id: string;
  referral_code_id: string;
  reward_credits: number;
  reward_type: 'signup_bonus' | 'first_purchase' | 'milestone_reward' | string;
  status: 'pending' | 'awarded' | 'cancelled' | string;
  awarded_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ReferralAnalytics {
  id: string;
  user_id: string;
  referral_period: string;
  total_referrals: number | null;
  successful_signups: number | null;
  total_credits_earned: number | null;
  conversion_rate: number | null;
  tier_level: 'bronze' | 'silver' | 'gold' | 'platinum';
  created_at: string | null;
  updated_at: string | null;
}

export interface ReferralStats {
  totalReferrals: number;
  successfulSignups: number;
  totalCreditsEarned: number;
  conversionRate: number;
  currentTier: 'bronze' | 'silver' | 'gold' | 'platinum';
  nextTierRequirement: number;
  recentRewards: ReferralReward[];
  leaderboardPosition?: number;
}

export interface ReferralTierConfig {
  tier: 'bronze' | 'silver' | 'gold' | 'platinum';
  minReferrals: number;
  signupBonus: number;
  firstPurchaseBonus: number;
  milestoneReward: number;
  benefits: string[];
}

export interface ReferralLeaderboardEntry {
  userId: string;
  userName: string;
  userAvatar?: string;
  totalReferrals: number;
  totalCreditsEarned: number;
  tier: 'bronze' | 'silver' | 'gold' | 'platinum';
  position: number;
}

export class ReferralSystemService {

  // Referral tier configuration
  private static readonly TIER_CONFIG: ReferralTierConfig[] = [
    {
      tier: 'bronze',
      minReferrals: 0,
      signupBonus: 10,
      firstPurchaseBonus: 25,
      milestoneReward: 50,
      benefits: ['10 credit signup bonus', '25 credit first purchase bonus']
    },
    {
      tier: 'silver',
      minReferrals: 5,
      signupBonus: 15,
      firstPurchaseBonus: 35,
      milestoneReward: 100,
      benefits: ['15 credit signup bonus', '35 credit first purchase bonus', '5% bonus on all rewards']
    },
    {
      tier: 'gold',
      minReferrals: 15,
      signupBonus: 20,
      firstPurchaseBonus: 50,
      milestoneReward: 200,
      benefits: ['20 credit signup bonus', '50 credit first purchase bonus', '10% bonus on all rewards', 'Exclusive rewards']
    },
    {
      tier: 'platinum',
      minReferrals: 50,
      signupBonus: 30,
      firstPurchaseBonus: 75,
      milestoneReward: 500,
      benefits: ['30 credit signup bonus', '75 credit first purchase bonus', '20% bonus on all rewards', 'Premium support', 'Early access to features']
    }
  ];

  /**
   * Generate referral code for user
   */
  static async generateReferralCode(customCode?: string): Promise<{
    success: boolean;
    referralCode?: string;
    error?: string;
  }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return {
          success: false,
          error: 'User not authenticated'
        };
      }

      // Check if user already has an active referral code
      const { data: existingCodes, error: existingError } = await supabase
        .from('referral_codes')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (existingError) {
        throw existingError;
      }

      // If user has existing active codes, return the first one (or deactivate duplicates)
      if (existingCodes && existingCodes.length > 0) {
        const primaryCode = existingCodes[0];
        
        // If there are multiple active codes, deactivate the extra ones
        if (existingCodes.length > 1) {
          console.log(`User ${user.id} has ${existingCodes.length} active referral codes, cleaning up duplicates`);
          const codesToDeactivate = existingCodes.slice(1);
          
          for (const codeToDeactivate of codesToDeactivate) {
            await supabase
              .from('referral_codes')
              .update({ is_active: false })
              .eq('id', codeToDeactivate.id);
          }
        }
        
        return {
          success: true,
          referralCode: primaryCode.referral_code
        };
      }

      // Generate new referral code
      let referralCode: string;
      if (customCode) {
        // Check if custom code is available
        const { data: codeCheck } = await supabase
          .from('referral_codes')
          .select('id')
          .eq('referral_code', customCode.toUpperCase())
          .limit(1);

        if (codeCheck && codeCheck.length > 0) {
          return {
            success: false,
            error: 'Referral code already exists'
          };
        }

        referralCode = customCode.toUpperCase();
      } else {
        // Generate random code
        referralCode = await this.generateUniqueCode();
      }

      // Create referral code in database
      const { data: newCode, error: insertError } = await supabase
        .from('referral_codes')
        .insert({
          user_id: user.id,
          referral_code: referralCode,
          is_active: true,
          uses_count: 0,
          max_uses: null, // Unlimited by default
          expires_at: null // No expiration by default
        })
        .select()
        .single();

      if (insertError) {
        throw insertError;
      }

      // Track referral code generation
      GTMService.trackCustomEvent('generate_referral_code', {
        referral_code: newCode.referral_code,
        custom_code: !!customCode
      }, user.id);

      return {
        success: true,
        referralCode: newCode.referral_code
      };
    } catch (error) {
      console.error('Generate referral code error:', error);
      return {
        success: false,
        error: 'Failed to generate referral code'
      };
    }
  }

  /**
   * Generate unique random referral code
   */
  private static async generateUniqueCode(): Promise<string> {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let attempts = 0;
    const maxAttempts = 10;

    while (attempts < maxAttempts) {
      let code = '';
      for (let i = 0; i < 8; i++) {
        code += characters.charAt(Math.floor(Math.random() * characters.length));
      }

      // Check if code already exists
      const { data: existingCode } = await supabase
        .from('referral_codes')
        .select('id')
        .eq('referral_code', code)
        .limit(1);

      if (!existingCode || existingCode.length === 0) {
        return code;
      }

      attempts++;
    }

    // Fallback with timestamp
    const timestamp = Date.now().toString(36).toUpperCase();
    return `REF${timestamp}`;
  }

  /**
   * Process referral signup - called when someone signs up with a referral code
   */
  static async processReferralSignup(
    referralCode: string,
    newUserId: string
  ): Promise<{
    success: boolean;
    creditsAwarded?: number;
    error?: string;
  }> {
    try {
      // Find the referral code
      const { data: codeDatas, error: codeError } = await supabase
        .from('referral_codes')
        .select('*')
        .eq('referral_code', referralCode.toUpperCase())
        .eq('is_active', true);

      if (codeError || !codeDatas || codeDatas.length === 0) {
        return {
          success: false,
          error: 'Invalid referral code'
        };
      }

      const codeData = codeDatas[0];

      // Check if code has expired
      if (codeData.expires_at && new Date(codeData.expires_at) < new Date()) {
        return {
          success: false,
          error: 'Referral code has expired'
        };
      }

      // Check if code has reached max uses
      if (codeData.max_uses && codeData.uses_count && codeData.uses_count >= codeData.max_uses) {
        return {
          success: false,
          error: 'Referral code has reached maximum uses'
        };
      }

      // Check if user is trying to refer themselves
      if (codeData.user_id === newUserId) {
        return {
          success: false,
          error: 'Cannot use your own referral code'
        };
      }

      // Check if user already has a referral reward (to prevent duplicates)
      const { data: existingReward, error: existingError } = await supabase
        .from('referral_rewards')
        .select('*')
        .eq('referred_user_id', newUserId)
        .limit(1);

      if (existingError) {
        console.error('Error checking existing referral reward:', existingError);
        return {
          success: false,
          error: 'Failed to check existing referral rewards'
        };
      }

      if (existingReward && existingReward.length > 0) {
        console.log('User already has a referral reward, skipping');
        return {
          success: true,
          creditsAwarded: 0,
          error: 'User already referred'
        };
      }

      // Get referrer's current tier
      const referrerTier = await this.getUserTier(codeData.user_id);
      const tierConfig = this.TIER_CONFIG.find(t => t.tier === referrerTier) || this.TIER_CONFIG[0];

      // Create reward record
      const { data: reward, error: rewardError } = await supabase
        .from('referral_rewards')
        .insert({
          referrer_id: codeData.user_id,
          referred_user_id: newUserId,
          referral_code_id: codeData.id,
          reward_credits: tierConfig.signupBonus,
          reward_type: 'signup_bonus',
          status: 'pending'
        })
        .select()
        .single();

      if (rewardError) {
        throw rewardError;
      }

      // Update referral code usage count
      await supabase
        .from('referral_codes')
        .update({
          uses_count: (codeData.uses_count || 0) + 1
        })
        .eq('id', codeData.id);

      // Award credits to referrer
      await this.awardCreditsToReferrer(codeData.user_id, tierConfig.signupBonus, reward.id);

      // Update referral analytics
      await this.updateReferralAnalytics(codeData.user_id);

      // Track referral conversion
      GTMService.trackReferralConverted(
        referralCode,
        codeData.user_id,
        newUserId
      );

      return {
        success: true,
        creditsAwarded: tierConfig.signupBonus
      };
    } catch (error) {
      console.error('Process referral signup error:', error);
      return {
        success: false,
        error: 'Failed to process referral signup'
      };
    }
  }

  /**
   * Process first purchase bonus
   */
  static async processFirstPurchaseBonus(userId: string): Promise<void> {
    try {
      // Check if user was referred
      const { data: referralRewards, error } = await supabase
        .from('referral_rewards')
        .select('*')
        .eq('referred_user_id', userId)
        .eq('reward_type', 'signup_bonus');

      if (error || !referralRewards || referralRewards.length === 0) {
        return; // User wasn't referred
      }

      const referralReward = referralRewards[0];

      // Check if first purchase bonus already awarded
      const { data: existingBonuses } = await supabase
        .from('referral_rewards')
        .select('id')
        .eq('referred_user_id', userId)
        .eq('reward_type', 'first_purchase');

      if (existingBonuses && existingBonuses.length > 0) {
        return; // Bonus already awarded
      }

      const referrerId = referralReward.referrer_id;
      const referrerTier = await this.getUserTier(referrerId);
      const tierConfig = this.TIER_CONFIG.find(t => t.tier === referrerTier) || this.TIER_CONFIG[0];

      // Create first purchase bonus
      const { data: bonus, error: bonusError } = await supabase
        .from('referral_rewards')
        .insert({
          referrer_id: referrerId,
          referred_user_id: userId,
          referral_code_id: referralReward.referral_code_id,
          reward_credits: tierConfig.firstPurchaseBonus,
          reward_type: 'first_purchase',
          status: 'pending'
        })
        .select()
        .single();

      if (bonusError) {
        throw bonusError;
      }

      // Award credits
      await this.awardCreditsToReferrer(referrerId, tierConfig.firstPurchaseBonus, bonus.id);

      // Update analytics
      await this.updateReferralAnalytics(referrerId);
    } catch (error) {
      console.error('Process first purchase bonus error:', error);
    }
  }

  /**
   * Award credits to referrer using new purchased credits system
   */
  private static async awardCreditsToReferrer(
    referrerId: string,
    credits: number,
    rewardId: string
  ): Promise<void> {
    try {
      console.log(`Awarding ${credits} credits to referrer ${referrerId}`);
      
      // Add credits directly to purchased_analysis_credits to work with new priority system
      const { error: updateError } = await supabase
        .from('subscription_limits')
        .upsert({
          user_id: referrerId,
          purchased_analysis_credits: supabase.raw(`COALESCE(purchased_analysis_credits, 0) + ${credits}`)
        }, {
          onConflict: 'user_id'
        });

      if (updateError) {
        throw updateError;
      }

      // Mark reward as awarded
      await supabase
        .from('referral_rewards')
        .update({
          status: 'awarded',
          awarded_at: new Date().toISOString()
        })
        .eq('id', rewardId);

      // Track referral reward earned
      GTMService.trackReferralRewardEarned(
        '', // Referral code not available in this context
        'credits',
        credits,
        referrerId
      );

      console.log(`✅ Referral credits awarded: ${credits} purchased credits added`);
    } catch (error) {
      console.error('Award credits to referrer error:', error);
      throw error;
    }
  }

  /**
   * Get user's referral statistics
   */
  static async getReferralStats(): Promise<ReferralStats> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Get referral code
      const { data: referralCodes, error: codeError } = await supabase
        .from('referral_codes')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (codeError) {
        throw codeError;
      }

      // Use the first active referral code if multiple exist
      const referralCode = referralCodes && referralCodes.length > 0 ? referralCodes[0] : null;

      // Get rewards
      const { data: rewards, error: rewardsError } = await supabase
        .from('referral_rewards')
        .select('*')
        .eq('referrer_id', user.id)
        .order('created_at', { ascending: false });

      if (rewardsError) {
        throw rewardsError;
      }

      const allRewards = rewards || [];
      const awardedRewards = allRewards.filter(r => r.status === 'awarded');
      
      const totalReferrals = referralCode?.uses_count || 0;
      const totalCreditsEarned = awardedRewards.reduce((sum, r) => sum + r.reward_credits, 0);
      const successfulSignups = awardedRewards.filter(r => r.reward_type === 'signup_bonus').length;
      const conversionRate = totalReferrals > 0 ? (successfulSignups / totalReferrals) * 100 : 0;

      // Get current tier
      const currentTier = await this.getUserTier(user.id);
      const nextTierIndex = this.TIER_CONFIG.findIndex(t => t.tier === currentTier) + 1;
      const nextTierRequirement = nextTierIndex < this.TIER_CONFIG.length 
        ? this.TIER_CONFIG[nextTierIndex].minReferrals - totalReferrals
        : 0;

      // Get recent rewards (last 10)
      const recentRewards = allRewards.slice(0, 10);

      return {
        totalReferrals,
        successfulSignups,
        totalCreditsEarned,
        conversionRate,
        currentTier,
        nextTierRequirement,
        recentRewards
      };
    } catch (error) {
      console.error('Get referral stats error:', error);
      throw error;
    }
  }

  /**
   * Get user's current tier based on successful referrals
   */
  static async getUserTier(userId: string): Promise<'bronze' | 'silver' | 'gold' | 'platinum'> {
    try {
      // Get successful signup referrals
      const { data: signupRewards, error } = await supabase
        .from('referral_rewards')
        .select('id')
        .eq('referrer_id', userId)
        .eq('reward_type', 'signup_bonus')
        .eq('status', 'awarded');

      if (error) {
        throw error;
      }

      const successfulReferrals = signupRewards?.length || 0;

      // Determine tier
      const tiers = [...this.TIER_CONFIG].reverse(); // Start from highest tier
      for (const tier of tiers) {
        if (successfulReferrals >= tier.minReferrals) {
          return tier.tier;
        }
      }

      return 'bronze'; // Default tier
    } catch (error) {
      console.error('Get user tier error:', error);
      return 'bronze';
    }
  }

  /**
   * Update referral analytics for user
   */
  private static async updateReferralAnalytics(userId: string): Promise<void> {
    try {
      const currentDate = new Date();
      const currentMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-01`;

      // Get current month stats
      const { data: rewards, error: rewardsError } = await supabase
        .from('referral_rewards')
        .select('*')
        .eq('referrer_id', userId)
        .eq('status', 'awarded');

      if (rewardsError) {
        throw rewardsError;
      }

      const allRewards = rewards || [];
      const totalReferrals = allRewards.filter(r => r.reward_type === 'signup_bonus').length;
      const totalCreditsEarned = allRewards.reduce((sum, r) => sum + r.reward_credits, 0);
      const conversionRate = totalReferrals > 0 ? 100 : 0; // Simplified for now

      const currentTier = await this.getUserTier(userId);

      // Upsert analytics record
      const { error: analyticsError } = await supabase
        .from('referral_analytics')
        .upsert({
          user_id: userId,
          referral_period: currentMonth,
          total_referrals: totalReferrals,
          successful_signups: totalReferrals,
          total_credits_earned: totalCreditsEarned,
          conversion_rate: conversionRate,
          tier_level: currentTier
        }, {
          onConflict: 'user_id, referral_period'
        });

      if (analyticsError) {
        throw analyticsError;
      }
    } catch (error) {
      console.error('Update referral analytics error:', error);
    }
  }

  /**
   * Get referral leaderboard
   */
  static async getReferralLeaderboard(limit: number = 50): Promise<ReferralLeaderboardEntry[]> {
    try {
      const { data: analytics, error } = await supabase
        .from('referral_analytics')
        .select(`
          user_id,
          total_referrals,
          total_credits_earned,
          tier_level
        `)
        .order('total_referrals', { ascending: false })
        .limit(limit);

      if (error) {
        throw error;
      }

      // Fetch user data separately for each user in the leaderboard
      const leaderboard: ReferralLeaderboardEntry[] = [];
      
      for (let i = 0; i < (analytics || []).length; i++) {
        const entry = analytics![i];
        
        // Get user data from the users table
        const { data: userData } = await supabase
          .from('users')
          .select('name, avatar')
          .eq('id', entry.user_id)
          .single();

        leaderboard.push({
          userId: entry.user_id,
          userName: userData?.name || 'Anonymous',
          userAvatar: userData?.avatar || undefined,
          totalReferrals: entry.total_referrals || 0,
          totalCreditsEarned: entry.total_credits_earned || 0,
          tier: (entry.tier_level as 'bronze' | 'silver' | 'gold' | 'platinum') || 'bronze',
          position: i + 1
        });
      }

      return leaderboard;
    } catch (error) {
      console.error('Get referral leaderboard error:', error);
      return [];
    }
  }

  /**
   * Claim milestone reward
   */
  static async claimMilestoneReward(milestone: number): Promise<{
    success: boolean;
    creditsAwarded?: number;
    error?: string;
  }> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return {
          success: false,
          error: 'User not authenticated'
        };
      }

      // Check if user has reached milestone
      const stats = await this.getReferralStats();
      if (stats.successfulSignups < milestone) {
        return {
          success: false,
          error: 'Milestone not reached'
        };
      }

      // Check if milestone already claimed
      const { data: existingReward } = await supabase
        .from('referral_rewards')
        .select('id')
        .eq('referrer_id', user.id)
        .eq('reward_type', `milestone_reward_${milestone}`)
        .single();

      if (existingReward) {
        return {
          success: false,
          error: 'Milestone already claimed'
        };
      }

      const tierConfig = this.TIER_CONFIG.find(t => t.tier === stats.currentTier) || this.TIER_CONFIG[0];

      // Get user's referral code ID for the milestone reward
      const { data: userReferralCodes } = await supabase
        .from('referral_codes')
        .select('id')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .limit(1);

      if (!userReferralCodes || userReferralCodes.length === 0) {
        return {
          success: false,
          error: 'No active referral code found'
        };
      }

      // Create milestone reward
      const { data: reward, error: rewardError } = await supabase
        .from('referral_rewards')
        .insert({
          referrer_id: user.id,
          referred_user_id: user.id, // Self-referral for milestone
          referral_code_id: userReferralCodes[0].id,
          reward_credits: tierConfig.milestoneReward,
          reward_type: `milestone_reward_${milestone}`,
          status: 'pending'
        })
        .select()
        .single();

      if (rewardError) {
        throw rewardError;
      }

      // Award credits
      await this.awardCreditsToReferrer(user.id, tierConfig.milestoneReward, reward.id);

      return {
        success: true,
        creditsAwarded: tierConfig.milestoneReward
      };
    } catch (error) {
      console.error('Claim milestone reward error:', error);
      return {
        success: false,
        error: 'Failed to claim milestone reward'
      };
    }
  }

  /**
   * Get tier configuration
   */
  static getTierConfig(): ReferralTierConfig[] {
    return this.TIER_CONFIG;
  }

  /**
   * Get sharing URL for referral code
   */
  static async getReferralSharingUrl(customMessage?: string): Promise<string | null> {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        return null;
      }

      const { data: referralCodes, error: codeError } = await supabase
        .from('referral_codes')
        .select('referral_code')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (codeError || !referralCodes || referralCodes.length === 0) {
        return null;
      }

      const referralCode = referralCodes[0];

      const baseUrl = window.location.origin;
      const message = customMessage || 'Join Rulevia and get AI-powered creative analysis!';
      
      // Track referral link generation (sharing intent)
      GTMService.trackReferralSent(
        referralCode.referral_code,
        'link',
        user.id
      );
      
      return `${baseUrl}/signup?ref=${referralCode.referral_code}&message=${encodeURIComponent(message)}`;
    } catch (error) {
      console.error('Get referral sharing URL error:', error);
      return null;
    }
  }
}

export default ReferralSystemService; 